import { useStore } from '@nanostores/react';
import { useEffect, useState } from 'react';

import {
  $activeHint,
  $anchorTick,
  $hintRegistry,
  dismissActiveHint,
  getFirstAnchor,
  type Hint,
} from '@/shared/stores';
import { FloatingOverlayPortal } from '@/shared/ui/primitives/floating-overlay';

import { Coachmark } from './Coachmark';
import { Spotlight } from './Spotlight';

/** Render-layer surface. Mount once near the root, inside a `FloatingOverlayHost`. */
export function HintHost() {
  const activeId = useStore($activeHint);
  const registry = useStore($hintRegistry);
  // Subscribe so the resolved DOM target updates when anchors mount/unmount.
  useStore($anchorTick);

  if (activeId === null) return null;
  const hint = registry.find((h) => h.id === activeId);
  if (hint === undefined) return null;

  return (
    <FloatingOverlayPortal>
      <ActiveHint key={hint.id} hint={hint} />
    </FloatingOverlayPortal>
  );
}

interface ActiveHintProps {
  readonly hint: Hint;
}

function ActiveHint({ hint }: ActiveHintProps) {
  const target = getFirstAnchor(hint.anchor);
  const rect = useAnchorRect(target);

  useEscapeToDismiss();

  if (target === null || rect === null) return null;

  return (
    <>
      <Spotlight rect={rect} />
      <Coachmark copy={hint.copy} onPrimary={dismissActiveHint} onSkip={dismissActiveHint} />
    </>
  );
}

function useAnchorRect(target: HTMLElement | null): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (target === null) return;
    const update = (): void => {
      setRect(target.getBoundingClientRect());
    };

    // ResizeObserver fires once on observe(), so we don't need a synchronous setRect here.
    const observer = new ResizeObserver(update);
    observer.observe(target);

    const scrollOptions = { capture: true, passive: true } as const;
    window.addEventListener('scroll', update, scrollOptions);
    window.addEventListener('resize', update);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', update, scrollOptions);
      window.removeEventListener('resize', update);
    };
  }, [target]);

  // Gate stale rect when the anchor unmounts before the new effect runs.
  return target === null ? null : rect;
}

function useEscapeToDismiss(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') dismissActiveHint();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, []);
}
